import { ProfilesClient, RareAlleleType, SampleClient, SampleGetDetailsResponse, SampleMarkerModel, SampleModel, SampleRandomMatchProbabilityModel } from '@app/api2/api';
import moment from 'moment';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { Grid, GridColumn, GridRow, Header, Icon, Message, Segment, Tab } from 'semantic-ui-react';
import { Button, ButtonGroup, Container, Menu } from 'semantic-ui-react';
import { buildUrl, AppRoutes } from '../AppRoutes';
import { areYouSure, areYouSure2 } from '../AreYouSure';
import { GenderSelectorSelect } from '../CaseRun/RunTypes/Brutus/GenderSelector';
import { toAlleleFormatName } from '@common/AlleleFormatNames';
import { BPPlotly } from '@common/BPPlotly';
import { BPColumn, BPReactTable } from '@common/BPReactTable';
import { BPSection } from '@common/BPSection';
import { InfoBox } from '@common/InfoBox';
import { PageHeader } from '@common/PageHeader';
import { convertCasesToLinks, downloadBlob, insertComponentsIntoText, toDate, toDateTime, toPartialDate, toSci } from '@common/Utils';
import { copyWithStyle } from '@app/components/CopyToClipboard';
import { BPBoxWithHeader } from '@common/BPBoxWithHeader';
import { RareAllelesFreqSelect } from '@common/RareAllelesFreqSelect';
import { sampleClient } from '@app/api2/Clients';
import useCreateCaseRunCompact from '../CaseRun/useCreateCaseRunCompact';
import { HandlerRegistry } from '../CaseRun/RunTypes/HandlerRegistry';
import { useTranslation2 } from '../Contexts/TranslationContext2';
import { useQuery } from '@tanstack/react-query';
import { BPButton } from '@/components/bp/bpbutton';
import { BPContainer } from '@/components/bp/bpcontainer';

export interface ISampleDetailsProps {
    id: number;
    labId: number;
    caseId?: number;
}
const client = sampleClient;
const searchTypes = ['dnamatch', 'familial_search'];


export default function SampleDetails(props: ISampleDetailsProps) {

    const { translations: t } = useTranslation2();

    const query = useQuery({
        queryKey: ['sampleDetails', props.labId, props.caseId, props.id],
        queryFn: async () => {
            const details = await client.getDetails(props.labId, props.caseId ?? 0, props.id);
            return details;
        },
        // keep previous data while fetching a new one when ids change
        placeholderData: prev => prev,
    });

    // const dialog = useDialog();

    const caseRunCompact = useCreateCaseRunCompact({
        labId: props.labId,
        caseId: props.caseId,
        referenceId: props.id,
        runType: 'dnamatch',
        onSuccess: (res) => { }
    });

    const caseRunCompactFamilial = useCreateCaseRunCompact({
        labId: props.labId,
        caseId: props.caseId,
        referenceId: props.id,
        runType: 'familial_search',
        onSuccess: (res) => { }
    });

    const deleteSample = React.useCallback(async () => {
        await areYouSure2({
            message: t.sampleView.confirmDelete,
            title: t.common.confirmDelete,
            action: async () => {
                await client.delete(props.labId, props.caseId ?? 0, props.id);
                await query.refetch();
            }
        });
    }, [client, props.labId, props.caseId, props.id]);

    const downloadSample = React.useCallback(async () => {
        const res = await client.download(props.labId, props.caseId ?? 0, props.id);
        downloadBlob({ data: res.data, fileName: res.fileName });
    }, [client, props.labId, props.caseId, props.id]);

    if (query.isLoading) return <p>Loading...</p>;
    if (query.isError) return <p>Error loading sample details</p>;

    const response = query.data as SampleGetDetailsResponse | undefined;
    if (!response) return null;

    const { sample } = response;
    const caseName = sample?.cases?.length > 0 ? sample.cases[0].name : '';

    // const caseRunCompact = useCreateCaseRunCompact({
    //     labId: props.labId,
    //     caseId: props.caseId, 
    //     referenceId: sample.id,
    //     runType: 'dnamatch',
    //     onSuccess: (res) => {
    //         // location.assign(buildUrl(Routes.CaseRunDetails, { labId: props.labId, caseId: props.caseId, caseRunId: res.id }));
    //     }
    // });



    return (
        <BPContainer>
            <PageHeader
                header={insertComponentsIntoText(window.translation.sampleView.details, {
                    '{sampleName}': sample.name,
                    '{caseName}': <Link key={'caseLink'} to={buildUrl(AppRoutes.CaseDetails, {
                        caseId: props.caseId,
                        labId: props.labId
                    })}>{caseName}</Link>
                })}
                buttons={sample != null && sample.active ? [
                    { label: t.common.edit, to: props.caseId ? buildUrl(AppRoutes.SampleEdit, { labId: props.labId, caseId: props.caseId, sampleId: sample.id }) : buildUrl(AppRoutes.SampleEditWithoutCase, { labId: props.labId, sampleId: sample.id }) },
                    { label: 'Create Copy', to: props.caseId ? buildUrl(AppRoutes.SampleCreateCopy, { labId: props.labId, caseId: props.caseId, sampleId: sample.id }) : buildUrl(AppRoutes.SampleCreateCopyWithoutCase, { labId: props.labId, sampleId: sample.id }) },
                    { label: t.common.download, action: async () => await downloadSample() },
                    { label: t.common.delete, negative: true, action: async () => await deleteSample() },
                    // { label: 'Create Sentry', action: async () => { caseRunCompact.show(); } },
                ] : [{
                    label: 'Restore', action: async () => {
                        await sampleClient.restore(props.labId, props.caseId ?? 0, props.id); 
                        await query.refetch();
                    }
                }]}
                renderRight={sample != null && !sample.active ? () =>
                    <Message warning>
                        <Header>{t.sampleView.deletedHeader}</Header>
                        {t.sampleView.deletedMessage}
                    </Message>
                    : null
                }></PageHeader>

            {sample != null &&
                <Grid>
                    <GridColumn width={16}>
                        <InfoBox header={t.common.information} items={[
                            { label: t.evidence.name, description: sample.name },
                            { label: t.common.kit, description: sample.kitName },
                            { label: t.common.created, description: sample.created ? moment(sample.created).format('l') : null },
                            { label: t.common.owner, description: sample.createdBy },
                            { label: t.common.alleleFormat, description: toAlleleFormatName(sample.alleleFormat) },
                            { label: t.brutus.sex, description: <GenderSelectorSelect showIcon={false} showLabel={true} readOnly name="sex" value={sample.sex} /> },
                            // { label: t.sample.dateOfBirth, description: toDate(sample.dateOfBirth) },
                            { label: t.sample.dateOfBirth, description: toPartialDate(sample.birthYear, sample.birthMonth, sample.birthDay) },
                            { label: t.sample.age, description: [(sample.ageYears != null ? `${sample.ageYears} ${t.sample.ageYears}` : ''), (sample.ageMonths != null ? `${sample.ageMonths} ${t.sample.ageMonths}` : '')].filter(x => x !== '').join(', ') },
                            { showIf: sample.cases?.length > 0, label: t.common.cases, description: convertCasesToLinks(sample.cases, props.labId) },
                        ]} />
                    </GridColumn>
                    <GridColumn width={4}>
                        <BPSection header={t.kit.markers}>
                            <BPReactTable
                                disablePagination
                                disableGlobalFilter
                                disableSortBy
                                stickyHeader
                                celled

                                data={sample.markers}
                                rowCssNameProvider={r => r.color}
                                columns={[
                                    { accessor: 'markerName', Header: t.kit.marker, collapsing: true },
                                    { id: 'allele1', accessor: 'alleles', collapsing: true, Header: t.sampleView.allele1, Cell: (cell) => <>{cell.value[0]?.allele}</> },
                                    { id: 'allele2', accessor: 'alleles', collapsing: true, Header: t.sampleView.allele2, Cell: (cell) => <>{cell.value[1]?.allele}</> },
                                    { id: 'id', Header: '', collapsing: false, Cell: () => <></> },
                                ] as BPColumn<SampleMarkerModel>[]}
                            />
                        </BPSection>
                    </GridColumn>
                    <GridColumn width={12}>
                        <BPBoxWithHeader header={'RMP'} buttons={[
                            { label: 'New RMP calculation', to: !props.caseId ? buildUrl(AppRoutes.SampleCalcRMPWithoutCase, { labId: props.labId, sampleId: sample.id }) : buildUrl(AppRoutes.SampleCalcRMP, { labId: props.labId, caseId: props.caseId, sampleId: sample.id }) },
                        ]}>
                            <BPReactTable
                                disablePagination
                                disableGlobalFilter
                                stickyHeader
                                celled
                                data={sample.randomMatchProbabilities}
                                columns={[
                                    { accessor: 'populationName', Header: t.common.population, collapsing: true, Cell: cell => <a href={buildUrl(AppRoutes.PopulationDetail, { populationId: cell.row.original.populationId })}>{cell.value}</a> },
                                    { accessor: 'fst', Header: t.parameter.names.fstCorrection, collapsing: true },
                                    { accessor: 'rareAlleleType', Header: t.parameter.names.rareAlleleFreqType, collapsing: true, Cell: cell => <RareAllelesFreqSelect readOnly name="r" value={cell.row.original.rareAlleleType} /> },
                                    { accessor: 'rmp', Header: 'RMP', Cell: cell => toSci(cell.value) },
                                    { accessor: 'oneDivRMP', Header: '1/RMP', Cell: cell => toSci(cell.value) },
                                    { accessor: 'createdAt', Header: t.common.created, Cell: cell => toDateTime(cell.value), collapsing: true },
                                ] as BPColumn<SampleRandomMatchProbabilityModel>[]}
                                renderRowSubComponent={row => <>
                                    <BPButton className='print-hide' icon onClick={() => {
                                        copyWithStyle(document.querySelector(`#tbl_${row.id}`));
                                    }}><Icon name={'copy'} /> {t.common.copyToClipboard}</BPButton>
                                    <div id={`tbl_${row.id}`}>
                                        <Segment basic>
                                            {row.reportText}
                                        </Segment>
                                        <Grid>
                                            <GridRow columns={3}>
                                                <GridColumn></GridColumn>
                                                <GridColumn>
                                                    <Segment basic>
                                                        <table className='dna'>
                                                            <tbody>
                                                                {sample.markers.map(marker => {
                                                                    return <tr key={`${row.id}_${marker.markerName}`}>
                                                                        <td>{marker.markerName}</td>
                                                                        <td>{marker.alleles?.at(0)?.allele}</td>
                                                                        <td>{marker.alleles?.at(-1)?.allele}</td>
                                                                    </tr>
                                                                })}
                                                            </tbody>
                                                        </table>
                                                    </Segment>
                                                </GridColumn>
                                                <GridColumn></GridColumn>
                                            </GridRow>
                                        </Grid>

                                        {row.skippedSystems?.length > 0 &&
                                            <Segment basic>

                                                Following systems are excluded from calculation: {row.skippedSystems.join(', ')}
                                            </Segment>
                                        }
                                    </div>


                                </>}
                            />
                        </BPBoxWithHeader>
                        {/* <dialog.Dialog /> */}
                        {caseRunCompact.element}
                        {caseRunCompactFamilial.element}
                        <BPBoxWithHeader header={t.common.searches} buttons={[
                            { label: 'New Sentry Search', action: async () => { caseRunCompact.show(); } },
                            { label: 'New Familial Search', action: async () => { caseRunCompactFamilial.show(); } },
                        ]}>
                            <Tab
                                menu={{ secondary: true, pointing: true }}
                                panes={
                                    searchTypes.filter(x => HandlerRegistry.hasOwnRunList(x)).map(rtype => {
                                        return { menuItem: t.runType[rtype], render: () => HandlerRegistry.renderRunList(rtype, { canCreateEvidence: true, caseId: props.caseId, labId: props.labId, referenceId: sample.id }) }
                                    })
                                } />
                        </BPBoxWithHeader>

                    </GridColumn>
                </Grid>
            }


        </BPContainer>
    )
}
